import { getCurrentUser } from "aws-amplify/auth";

import { Authenticator } from "@aws-amplify/ui-react";

import "@aws-amplify/ui-react/styles.css";
import Clarity from "@microsoft/clarity";
import { useAtom, useAtomValue } from "jotai";
import { useEffect } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { debugModeAtom } from "./atoms/debugModeAtom";
import { userAtom, userLoadingAtom } from "./atoms/userAtom";
import LocationProvider from "./common/LocationProvider";
import params from "./constants/authenticatorParams";
import "./index.css";
import { HomePage } from "./pages/HomePage";
import { SavedPage } from "./pages/SavedPage";
import { TermsPage } from "./pages/TermsPage";
import { VisitedPage } from "./pages/VisitedPage";
import { ProfilePage } from "./pages/onboarding/ProfilePage";
import { LineLinkPage } from "./pages/onboarding/LineLinkPage";
import { initGA, setUserId, setUserProperties, trackPageView } from "./services/analytics";
import { useProfileCheck } from "./hooks/useProfileCheck";

function AuthenticatedApp() {
  const [user, setUser] = useAtom(userAtom);
  const [userLoading, setLoading] = useAtom(userLoadingAtom);
  const debugMode = useAtomValue(debugModeAtom);
  const location = useLocation();
  const navigate = useNavigate();

  const targetEnv = import.meta.env.VITE_APP_ENV;

  // プロフィールチェック
  // エラー時はフェイルオープン（ホーム画面へ遷移）
  const { isProfileIncomplete, isLoading: isProfileLoading } = useProfileCheck();

  useEffect(() => {
    initGA(debugMode || targetEnv !== "prd");

    if (targetEnv === "prd") {
      Clarity.init("rxpun56rw9");
    }
  }, [targetEnv, debugMode]);

  useEffect(() => {
    if (targetEnv === "prd" && user?.userId) {
      Clarity.identify(user.userId);
    }
  }, [targetEnv, user?.userId]);

  useEffect(() => {
    if (user?.userId) {
      setUserId(user.userId);
      setUserProperties({
        user_id: user.userId,
        user_status: "authenticated",
        username: user.username || "unknown"
      });
    } else {
      setUserProperties({
        user_status: "not_authenticated"
      });
    }
  }, [user]);

  useEffect(() => {
    const loadUser = async () => {
      try {
        setLoading(true);
        const user = await getCurrentUser();
        setUser({
          userId: user.userId,
          username: user.username
        });
      } catch (error) {
        console.warn("No user session found:", error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [setUser, setLoading]);

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  // プロフィールチェック
  useEffect(() => {
    // プロフィール読み込み中またはユーザー情報読み込み中はスキップ
    if (isProfileLoading || userLoading) {
      return;
    }

    // 既にオンボーディングページにいる場合はスキップ
    if (location.pathname.startsWith("/onboarding/")) {
      return;
    }

    // プロフィール未完了の場合、オンボーディングページへリダイレクト
    if (isProfileIncomplete) {
      navigate("/onboarding/profile", { replace: true });
    }
  }, [isProfileIncomplete, isProfileLoading, userLoading, location.pathname, navigate]);

  // ユーザー情報が読み込み中の場合はローディング画面を表示
  if (userLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <img src="/logo-icon.svg" className="h-20 w-20 animate-pulse" alt="Loading" />
          <span className="text-xl text-muted-foreground">読み込み中...</span>
        </div>
      </div>
    );
  }

  return (
    <Authenticator {...params}>
      {({ user: amplifyUser }) => {
        if (amplifyUser && !user) {
          const loadUser = async () => {
            try {
              const currentUser = await getCurrentUser();
              setUser({
                userId: currentUser.userId,
                username: currentUser.username
              });
            } catch (error) {
              console.warn("Failed to reload user after authentication:", error);
            }
          };
          loadUser();
        }

        return (
          <>
            <LocationProvider />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/visited" element={<VisitedPage />} />
              <Route path="/saved" element={<SavedPage />} />
              <Route path="/onboarding/profile" element={<ProfilePage />} />
              <Route path="/onboarding/line" element={<LineLinkPage />} />
            </Routes>
          </>
        );
      }}
    </Authenticator>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/terms" element={<TermsPage />} />
      <Route path="*" element={<AuthenticatedApp />} />
    </Routes>
  );
}

export default App;
